import type { TFunction } from 'i18next';
import type React from 'react';
import { Icon } from '../components/Core/Icon';
import { AuthenticatedUserMenuActionEnum } from '../enums/AuthenticatedUserMenuActionEnum';
import { isDevEnvironment } from '../utils/utils';

/**
 * Context describing the state for determining
 * available menu actions for the authenticated user menu.
 */
export interface AuthenticatedUserMenuActionContext {
  pathname: string;
  isExternalOrReader: boolean;
  isOnBehalfOfUser: boolean;
  canShowImpersonate: boolean;
}

/**
 * A child menu action (used for submenu items, e.g. Security).
 */
export interface AuthenticatedUserMenuActionChild {
  key: string;
  /** Static label (e.g. language names). Use `labelKey` for i18n. */
  label?: string;
  /** i18n key; rendered with `t(labelKey)` when set. */
  labelKey?: string;
  icon?: React.ReactNode;
  condition?: (ctx: AuthenticatedUserMenuActionContext) => boolean;
  isActive?: (ctx: AuthenticatedUserMenuActionContext) => boolean;
}

/**
 * Structure of a user menu action, top-level or submenu.
 */
export interface AuthenticatedUserMenuAction {
  key: AuthenticatedUserMenuActionEnum;
  labelKey?: string;
  // If defined, renders a custom label (e.g. for highlighting)
  getLabel?: (ctx: AuthenticatedUserMenuActionContext, t: TFunction) => React.ReactNode;
  icon?: React.ReactNode;
  danger?: boolean;
  type?: 'divider' | 'submenu';
  // Only display when condition is met (receives context)
  condition?: (ctx: AuthenticatedUserMenuActionContext) => boolean;
  // Submenu children
  children?: AuthenticatedUserMenuActionChild[];
}

/**
 * All menu actions/options for the authenticated user.
 * Menu construction is dynamic - options may display or hide based on props or context.
 * 'condition' is used to determine visibility.
 */
export const authenticatedUserMenuActions: AuthenticatedUserMenuAction[] = [
  // Account hub (profile / preferences / security / sessions) — first action after role header
  {
    key: AuthenticatedUserMenuActionEnum.Account,
    labelKey: 'account.title',
    icon: <Icon name="UserCogIcon" className="h-5 w-5" />,
    condition: () => true,
  },
  // Divider for menu grouping
  {
    key: AuthenticatedUserMenuActionEnum.Divider,
    type: 'divider',
  },
  // "Stop Impersonation" action: Only while impersonating another user
  {
    key: AuthenticatedUserMenuActionEnum.StopImpersonation,
    labelKey: 'common.stopImpersonation',
    icon: <Icon name="UserXIcon" className="h-5 w-5" />,
    danger: true,
    condition: (ctx) => ctx.isOnBehalfOfUser,
  },
  // "Impersonate User" action: Only if impersonation is permitted
  {
    key: AuthenticatedUserMenuActionEnum.ImpersonateUser,
    labelKey: 'common.impersonateUser',
    icon: <Icon name="UserIcon" className="h-5 w-5" />,
    condition: (ctx) => ctx.canShowImpersonate,
  },
  // Divider for menu grouping
  {
    key: AuthenticatedUserMenuActionEnum.Divider,
    type: 'divider',
  },
  {
    key: AuthenticatedUserMenuActionEnum.AppDebugDrawer,
    labelKey: 'common.debug',
    icon: <Icon name="BugIcon" className="text-yellow-dark h-5 w-5" />,
    condition: () => isDevEnvironment(),
  },
  // "Sign Out" action (always shown)
  {
    key: AuthenticatedUserMenuActionEnum.SignOut,
    labelKey: 'common.signOut',
    danger: true,
    icon: <Icon name="LogOutIcon" className="h-5 w-5" />,
    condition: () => true,
  },
];

/**
 * Recursively filter and clean menu actions based on context.
 * - Only includes actions where 'condition' returns true (or is omitted).
 * - Submenus are only included if they have children.
 * - Collapses consecutive dividers and removes trailing divider.
 */
function filterActionsRecursive(
  actions: AuthenticatedUserMenuAction[],
  context: AuthenticatedUserMenuActionContext,
): AuthenticatedUserMenuAction[] {
  // Filter by condition field; submenu children may each have their own condition.
  const filtered = actions
    .filter((a) => !a.condition || a.condition(context))
    .map((a) => {
      if (a.type === 'submenu' && Array.isArray(a.children)) {
        return {
          ...a,
          children: a.children.filter((c) => !c.condition || c.condition(context)),
        };
      }
      return { ...a, children: a.children };
    })
    .filter((a) => {
      // Only include submenus if they have at least one child.
      if (a.type === 'submenu') {
        return Array.isArray(a.children) && a.children.length > 0;
      }
      return true;
    });

  // Remove consecutive dividers and trailing divider
  const cleaned: AuthenticatedUserMenuAction[] = [];
  for (const action of filtered) {
    if (action.type === 'divider') {
      const prev = cleaned[cleaned.length - 1];
      // Skip if divider is first item or follows another divider.
      if (!prev || prev.type === 'divider') continue;
    }
    cleaned.push(action);
  }
  // Remove divider if it's the last action
  if (cleaned.length && cleaned[cleaned.length - 1]?.type === 'divider') {
    cleaned.pop();
  }

  return cleaned;
}

/**
 * Returns the list of header profile menu actions filtered by context,
 * ensuring:
 *   - Only actions relevant to the current context are shown.
 *   - No consecutive dividers (or divider at start/end).
 *   - Submenus only shown if containing children.
 */
export function filteredAuthenticatedUserMenuActions(
  context: AuthenticatedUserMenuActionContext,
): AuthenticatedUserMenuAction[] {
  return filterActionsRecursive(authenticatedUserMenuActions, context);
}
